import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { FirstWeekResponse, FirstWeekStepKey, Organization } from 'aeo-schema-generator-shared/types'
import { apiService } from '@/services/api'
import { OnboardingWizard } from '@/components/aba/OnboardingWizard'
import { Button, Dialog, Notice } from '@/design-system/primitives'
import { FirstWeekInviteDialog } from './FirstWeekInviteDialog'
import { FirstWeekProfileEditor, findProfileForDomain } from './FirstWeekProfileEditor'
import { buildReportPath } from '@/utils/accountScan'
import { connectFromDashboard } from '@/utils/hubspotReturnTo'
import { readChosenPage } from '@/hooks/useHubSpotOnboarding'
import { withWriteNow } from '@/pages/unifiedPageDetailRouting'
import {
  BUSY_LINES,
  PAGE_UNREACHABLE,
  PROFILE_LOOKUP_FAILED,
  SETUP_NEEDS_ADMIN,
  SETUP_UNAVAILABLE,
  SITE_LIMIT_REACHED,
  WATCH_CAP_AFTER,
  WATCH_CAP_BEFORE,
  WATCH_CAP_LINK,
  WATCH_FAILED,
  SETUP_NO_SITE,
  SETUP_DOMAIN_MISSING,
  SETUP_AMBIGUOUS,
  PROFILE_FIRST_TITLE,
  PROFILE_FIRST_LEAD,
  PROFILE_FIRST_GO,
  PROFILE_FIRST_SKIP,
  TEAMMATE_WAITS_FOR_SITE,
  liveStepKey,
} from './firstWeekCopy'

export interface StepRequest {
  key: FirstWeekStepKey
  /** A counter so the same step can be asked for twice in a row. */
  at: number
}

interface ActionNotice {
  text: string
  /** A place to go and fix it, when there is one. */
  link?: { to: string; label: string; after: string }
  /** Nothing went wrong: something is waited on. Drawn as a plain notice, not an alarm. */
  calm?: boolean
}

const START_PATH = '/welcome/start'
const PLANS_PATH = '/settings/billing?section=plans#subscription-plans'

/*
  Carries out a step from the guide, in place. The dashboard hands it the
  step the person chose; it opens the right dialog or navigates, and calls
  onChanged when something on the server may have changed so the guide
  refetches. Failures are sentences in a Notice, never toasts of raw errors,
  and a step that calls the server first says so while it waits.

  Adding a site is the one step it does not carry out itself: the dashboard
  already owns that dialog, so the request is handed back through onAddSite.

  An account with no plan walks a different guide, and its steps go to
  different places: the site is added on the start page, the report step
  opens the report, the free page opens that page's own detail, HubSpot is
  connected from here and comes back here, and watching leads to the plans.

  The report is the team's, and the guide's own answer names its scan. So it
  opens for a teammate as it does for the owner, and nothing here asks the
  server whose scan it is: the person asking may have claimed none.

  The start page is the owner's. It sends anybody who does not own their team
  straight back to the dashboard, so a teammate whose team has nothing to
  open yet is told what is waited on, and is sent nowhere.
*/
export function FirstWeekActions({
  guide,
  request,
  onChanged,
  onHandled,
  onAddSite,
}: {
  guide: FirstWeekResponse
  request: StepRequest | null
  onChanged: () => void
  onHandled: () => void
  onAddSite?: () => void
}) {
  const navigate = useNavigate()
  const [profileOpen, setProfileOpen] = useState(false)
  const [existingOrg, setExistingOrg] = useState<Organization | null>(null)
  const [pixelOpen, setPixelOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [notice, setNotice] = useState<ActionNotice | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // Set when somebody reaches for a page before the profile is confirmed.
  const [askProfileFirst, setAskProfileFirst] = useState(false)
  /*
    Null, not zero: a request numbered 0 is a real request, and starting at
    zero would swallow it.
  */
  const handled = useRef<number | null>(null)
  /* True from the moment a step starts until it has finished, whichever way it ends. */
  const running = useRef(false)

  const domain = guide.domain?.domain ?? null
  // The guide already worked out whether the profile is confirmed; read its
  // answer rather than deciding again from different facts.
  const profileConfirmed = guide.steps.find((s) => s.key === 'profile')?.state === 'done'
  /* An account with no plan walks a different guide: its site is added where
     the report begins, and watching leads to the plans. */
  const free = guide.variant === 'free'
  /*
    The page was put on the site and has not been seen there yet, so the
    step's button reads "Check again". The trial calls that step `live` and
    the free guide calls it `free_page`; the same check serves both.
  */
  const awaitingRecheck = guide.steps.find((s) => s.key === liveStepKey(guide.variant))?.state === 'not_seen_yet' && !!guide.firstPageUrl
  /*
    The guide shows a Connect HubSpot step and it is not done. An owner who
    invites somebody else instead has no such step, and nothing here says
    whether HubSpot is connected, so this stays false for them.
  */
  const hubspotWaiting = guide.steps.some((s) => s.key === 'hubspot' && s.state !== 'done')
  /*
    The team's report, when it has one: where a free account sees its page
    beside what AI is told about it. The scan comes with the guide, so the
    address is known before anything is pressed.
  */
  const reportPath = guide.reportScanId && guide.domain ? buildReportPath(guide.reportScanId, guide.domain.domain) : null
  /*
    The free page step opens the page's own detail: the report's scan names
    the page, and the server says whether it is free. HubSpot opens for the owner,
    with HubSpot connected when that installation method is used. Manual
    installation also opens page detail for teammates so they can copy the code.
  */
  const opensPageDetail = !!reportPath && !hubspotWaiting && (guide.installationMethod === 'manual' || guide.viewer !== 'teammate')
  /*
    A teammate whose team has no report to open and no page set up. The owner
    invited them before entering a site, and the start page, where a site is
    entered, turns a teammate away.
  */
  const teammateWaitsForSite = guide.viewer === 'teammate' && !reportPath && !guide.firstPageUrl

  /* To the start page: it asks for the site, or reads the one already here. Never for a teammate it would turn away. */
  function goToStart() {
    if (teammateWaitsForSite) {
      setNotice({ text: TEAMMATE_WAITS_FOR_SITE, calm: true })
      return
    }
    navigate(START_PATH)
  }

  /*
    What to say while a step works. Only a step that waits on the server
    says anything: a line shown for a step that only goes somewhere has
    nothing under way to end it. The free guide does different things under
    some of the same buttons, so it says something else for them.
  */
  function busyLine(key: FirstWeekStepKey): string | null {
    // Leads to the plans. Nothing is being turned on.
    if (free && key === 'watch') return null
    // Goes to the report, or to the start page. Nothing is waited on.
    if (key === 'report') return null
    if (key === 'free_page') {
      if (awaitingRecheck) return BUSY_LINES.live ?? null
      // The page is asked about and found before it opens.
      if (opensPageDetail) return BUSY_LINES.free_page ?? null
      // With a report it goes to connect HubSpot, or to the report. Nothing is waited on.
      if (reportPath) return null
      // No report, and a page set up before there were reports: its setup is looked up.
      // With neither it goes to the start page, and nothing is waited on.
      return guide.firstPageUrl ? BUSY_LINES.first_page ?? null : null
    }
    return BUSY_LINES[key] ?? null
  }

  /*
    Carry out one step: clear the last message, say what is under way, go.

    One step at a time. A double click used to run the same step twice: two
    lookups and two navigations, so Back landed on the page it had just left.
    A second ask while one is under way is dropped.
  */
  function startStep(key: FirstWeekStepKey) {
    if (running.current) return
    running.current = true
    setNotice(null)
    setBusy(busyLine(key))
    void run(key).finally(() => {
      running.current = false
      setBusy(null)
    })
  }

  useEffect(() => {
    if (!request || handled.current === request.at) return
    handled.current = request.at
    startStep(request.key)
    onHandled()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request])

  async function run(key: FirstWeekStepKey) {
    switch (key) {
      case 'add_site':
        if (free) goToStart()
        else onAddSite?.()
        return
      case 'report':
        // With no report yet the start page takes over: it asks for the site, or reads the one already here.
        if (reportPath) navigate(reportPath)
        else goToStart()
        return
      case 'free_page':
        if (awaitingRecheck) {
          await checkAgain()
          return
        }
        /*
          With a report, the page goes live through HubSpot, so this goes
          where the Connect HubSpot step goes first. Once HubSpot is
          connected it opens the page's own detail, where what we wrote can
          be read and then sent. When the free page's detail cannot be
          opened and a page is already written, that page's detail opens
          instead: sent and not yet confirmed, the first page is no longer
          free, and the report is not where a written page lives. Only with
          nothing written, or a written page that cannot be opened, is the
          report where this goes: it is still there, and still puts the
          page live.
        */
        if (reportPath) {
          if (hubspotWaiting) navigate(connectFromDashboard(domain))
          else if (opensPageDetail && guide.reportScanId) navigate((await freePageDetail(guide.reportScanId)) ?? (await writtenPageDetail()) ?? reportPath)
          else navigate(reportPath)
          return
        }
        /*
          One account has no report and nothing to start: it set a page up
          before sign-up came first, so it never ran the read, and the start
          page would send it straight back. Its page has a setup screen of
          its own, and that is where this step sends it, whatever the Connect
          HubSpot step says: with no report there is nothing for a
          connection made from here to come back to.
        */
        if (guide.firstPageUrl) {
          await goToSetup(guide.firstPageUrl.url)
          return
        }
        // No report and no page: the start page asks for the site, or reads the one already here.
        goToStart()
        return
      case 'profile': {
        let found: Organization | null
        try {
          found = await findProfileForDomain(domain)
        } catch {
          /*
            A missing profile comes back as data: null, so a throw is the
            server failing. Opening the form here would create a second
            profile for a business that already has one.
          */
          setNotice({ text: PROFILE_LOOKUP_FAILED })
          return
        }
        setExistingOrg(found)
        setProfileOpen(true)
        return
      }
      case 'hubspot':
        navigate(free ? connectFromDashboard(domain) : '/hubspot')
        return
      case 'invite':
        setInviteOpen(true)
        return
      case 'first_page': {
        if (!domain) {
          setNotice({ text: SETUP_NO_SITE })
          return
        }
        /*
          The schema we write leans on the profile: without one we guess the
          business details off the page. Say so once, and let them decide.
        */
        if (!profileConfirmed) {
          setAskProfileFirst(true)
          return
        }
        await goToSetup(`https://${domain}/`)
        return
      }
      case 'live': {
        if (awaitingRecheck) {
          await checkAgain()
          return
        }
        if (guide.firstPageUrl) await goToSetup(guide.firstPageUrl.url)
        else if (domain) await goToSetup(`https://${domain}/`)
        return
      }
      case 'pixel':
        setPixelOpen(true)
        return
      case 'watch': {
        if (free) {
          navigate(PLANS_PATH)
          return
        }
        if (!guide.liveUrl) return
        try {
          const res = await apiService.activateMonitoring(guide.liveUrl.urlId)
          const data = res.data
          if (data && !data.activated && data.reason === 'cap_reached') setNotice(capNotice(data.cap))
        } catch (error: any) {
          /*
            The server answers a full plan with a 409, so the client library
            rejects and the answer arrives here, as response.data.data.
          */
          const body = error?.response?.status === 409 ? error?.response?.data?.data : null
          if (body?.reason === 'cap_reached') setNotice(capNotice(body.cap))
          else setNotice({ text: WATCH_FAILED })
        }
        onChanged()
        return
      }
    }
  }

  /*
    Where the free first page is written and sent: its own page detail,
    asked to write it on arrival. The page is the one the person chose on
    the report in this tab, when they chose one, and the report's own pick
    otherwise. Null whenever there is no such place to send them: the first
    page is not free, no page is named, the page has no detail yet, or the
    server could not say. The caller then opens the page that is already
    written, or with none the report, so a failure here is never a sentence
    and never a dead button.
  */
  async function freePageDetail(scanId: string): Promise<string | null> {
    try {
      const answer = (await apiService.getOnboardingFreePage(scanId))?.data
      if (answer?.eligible !== true) return null
      const url = readChosenPage(scanId) ?? answer.url
      if (!url) return null
      const pageDetailUrl = (await apiService.resolvePageSetupUrl(url))?.data?.pageDetailUrl
      return pageDetailUrl ? withWriteNow(pageDetailUrl) : null
    } catch {
      return null
    }
  }

  /*
    Where the page that is already written can be read and sent: its own
    page detail, opened as it is, with nothing asked of it on arrival. Only
    a detail counts. A setup screen is where a page is written, and this
    one is. Null when no page is written, or it has no detail to open, or
    the server could not say: the caller opens the report then.
  */
  async function writtenPageDetail(): Promise<string | null> {
    if (!guide.firstPageUrl) return null
    try {
      return (await apiService.resolvePageSetupUrl(guide.firstPageUrl.url))?.data?.pageDetailUrl ?? null
    } catch {
      return null
    }
  }

  /* Reads the page once more, in place: it was put on the site and has not been seen there yet. */
  async function checkAgain() {
    if (!guide.firstPageUrl) return
    try {
      await apiService.detectDeployment(guide.firstPageUrl.urlId, 'immediate')
    } catch {
      setNotice({ text: PAGE_UNREACHABLE })
    }
    onChanged()
  }

  function capNotice(cap: number | undefined): ActionNotice {
    return {
      text: WATCH_CAP_BEFORE(cap),
      link: { to: domain ? `/domains/${domain}/monitoring` : '/dashboard', label: WATCH_CAP_LINK, after: WATCH_CAP_AFTER },
    }
  }

  async function goToSetup(url: string) {
    try {
      const res = await apiService.resolvePageSetupUrl(url)
      const data = res.data
      // A page we already know about comes back as a detail URL rather than a
      // setup URL. Both are somewhere to go; only one of them was read here.
      const target = data?.setupUrl ?? data?.pageDetailUrl
      if (target) {
        navigate(target)
        return
      }
      if (data?.status === 'domain_limit_reached') setNotice({ text: SITE_LIMIT_REACHED })
      else if (data?.status === 'permission_required') setNotice({ text: SETUP_NEEDS_ADMIN })
      else if (data?.status === 'domain_not_found') setNotice({ text: SETUP_DOMAIN_MISSING })
      else if (data?.status === 'ambiguous_match') setNotice({ text: SETUP_AMBIGUOUS })
      else setNotice({ text: SETUP_UNAVAILABLE })
    } catch {
      setNotice({ text: SETUP_UNAVAILABLE })
    }
  }

  return (
    <>
      {busy && <Notice kind="info">{busy}</Notice>}
      {notice && (
        <Notice kind={notice.calm ? 'info' : 'look'}>
          <p>
            {notice.text}
            {notice.link && (
              <>
                <Link to={notice.link.to}>{notice.link.label}</Link>
                {notice.link.after}
              </>
            )}
          </p>
        </Notice>
      )}
      <Dialog
        open={askProfileFirst}
        onClose={() => setAskProfileFirst(false)}
        title={PROFILE_FIRST_TITLE}
        lead={PROFILE_FIRST_LEAD}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setAskProfileFirst(false)
                if (domain) void goToSetup(`https://${domain}/`)
              }}
            >
              {PROFILE_FIRST_SKIP}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setAskProfileFirst(false)
                startStep('profile')
              }}
            >
              {PROFILE_FIRST_GO}
            </Button>
          </>
        }
      />
      <FirstWeekProfileEditor
        open={profileOpen}
        domain={domain}
        existing={existingOrg}
        prefill={guide.profilePrefill}
        onClose={() => setProfileOpen(false)}
        onSaved={() => {
          setProfileOpen(false)
          onChanged()
        }}
        onFailed={(text) => setNotice({ text })}
      />
      {domain && (
        <OnboardingWizard
          open={pixelOpen}
          onOpenChange={setPixelOpen}
          onComplete={() => {
            setPixelOpen(false)
            onChanged()
          }}
          skipWelcome
          lockedDomain={domain}
        />
      )}
      <FirstWeekInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onChanged={onChanged}
        planName={guide.trial?.planName ?? null}
        variant={guide.variant}
      />
    </>
  )
}
